import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type PriceDisplayFormat = 'percentage' | 'american' | 'price' | 'decimal';

type MarketSettingsState = {
  priceFormat: PriceDisplayFormat;

  isSettingsOpen: boolean;

  setPriceFormat: (format: PriceDisplayFormat) => void;
  openSettings: () => void;
  closeSettings: () => void;
  reset: () => void;
};

const initialState: Pick<MarketSettingsState, 'priceFormat' | 'isSettingsOpen'> = {
  priceFormat: 'percentage',
  isSettingsOpen: false,
};

export const PRICE_FORMAT_OPTIONS: { id: PriceDisplayFormat; label: string }[] = [
  { id: 'percentage', label: 'Percentage' },
  { id: 'american', label: 'American' },
  { id: 'price', label: 'Price' },
  { id: 'decimal', label: 'Decimal' },
];

export const useMarketSettingsStore = create<MarketSettingsState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setPriceFormat: (format) => set({ priceFormat: format }, false, 'setPriceFormat'),

        openSettings: () => set({ isSettingsOpen: true }, false, 'openSettings'),

        closeSettings: () => set({ isSettingsOpen: false }, false, 'closeSettings'),

        reset: () => set(initialState, false, 'reset'),
      }),
      {
        name: 'market-settings',
        partialize: (state) => ({ priceFormat: state.priceFormat }),
      }
    ),
    { name: 'MarketSettingsStore' }
  )
);

export function usePriceFormat(): PriceDisplayFormat {
  return useMarketSettingsStore((state) => state.priceFormat);
}

export function useSetPriceFormat(): (format: PriceDisplayFormat) => void {
  return useMarketSettingsStore((state) => state.setPriceFormat);
}

export function useIsSettingsOpen(): boolean {
  return useMarketSettingsStore((state) => state.isSettingsOpen);
}

export function useOpenSettings(): () => void {
  return useMarketSettingsStore((state) => state.openSettings);
}

export function useCloseSettings(): () => void {
  return useMarketSettingsStore((state) => state.closeSettings);
}
